import { useState, useCallback, useRef, useEffect } from 'react';

// API_BASE should point to the main web app (apps/web) which hosts the site-agent API
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

// localStorage key prefix
const STORAGE_KEY_PREFIX = 'site-agent-conversation';

/**
 * Get localStorage key for a brand
 */
function getStorageKey(brandId, brandSlug) {
  if (brandId) return `${STORAGE_KEY_PREFIX}-${brandId}`;
  if (brandSlug) return `${STORAGE_KEY_PREFIX}-${brandSlug}`;
  return `${STORAGE_KEY_PREFIX}-global`;
}

/**
 * Load conversation state from localStorage
 */
function loadFromStorage(key) {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Only restore if less than 24 hours old
      if (parsed.lastUpdated && Date.now() - parsed.lastUpdated < 24 * 60 * 60 * 1000) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Failed to load conversation from localStorage:', e);
  }
  return null;
}

/**
 * Save conversation state to localStorage
 */
function saveToStorage(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({
      ...data,
      lastUpdated: Date.now(),
    }));
  } catch (e) {
    console.warn('Failed to save conversation to localStorage:', e);
  }
}

/**
 * Load conversation from database (fallback if localStorage is empty)
 */
async function loadFromDB(brandId) {
  if (!brandId) return null;
  try {
    const response = await fetch(`${API_BASE}/api/brands/${brandId}/conversations`, {
      credentials: 'include',
    });
    if (!response.ok) return null;
    const { conversation } = await response.json();
    if (conversation) {
      return {
        conversationId: conversation.id,
        status: conversation.status === 'completed' ? 'done' : conversation.status,
        messages: conversation.messages || [],
        steps: conversation.steps || [],
        changedFiles: conversation.changed_files || [],
        lastUpdated: new Date(conversation.updated_at).getTime(),
      };
    }
  } catch (e) {
    console.warn('Failed to load conversation from DB:', e);
  }
  return null;
}

/**
 * Save conversation to database (non-blocking)
 */
async function saveToDB(brandId, data) {
  if (!brandId) return null;
  try {
    const response = await fetch(`${API_BASE}/api/brands/${brandId}/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        conversationId: data.conversationId,
        status: data.status,
        messages: data.messages,
        steps: data.steps,
        changedFiles: data.changedFiles,
        lastError: data.error,
      }),
    });
    if (!response.ok) {
      // Don't try to read body if not ok - just log the status
      console.warn('Failed to save conversation to DB: HTTP', response.status);
      return null;
    }
    // Only read JSON if response is ok
    const result = await response.json();
    console.debug('[Agent] Conversation saved to DB:', result.action);
    return result;
  } catch (e) {
    // Silently fail - this is non-critical
    console.debug('[Agent] DB save skipped:', e.message);
    return null;
  }
}

/**
 * Hook for interacting with the Site Agent via SSE
 * 
 * @param {Object} config - Configuration options
 * @param {string} config.brandId - Brand UUID for per-brand sandbox
 * @param {string} config.brandSlug - Brand slug for sandbox path resolution
 * @returns {Object} Agent state and controls
 */
export function useSiteAgent({ brandId, brandSlug } = {}) {
  const storageKey = getStorageKey(brandId, brandSlug);
  
  // Initialize state from localStorage
  const [status, setStatus] = useState(() => {
    const stored = loadFromStorage(storageKey);
    return stored?.status === 'running' ? 'idle' : (stored?.status || 'idle');
  });
  const [steps, setSteps] = useState(() => {
    const stored = loadFromStorage(storageKey);
    return stored?.steps || [];
  });
  const [changedFiles, setChangedFiles] = useState(() => {
    const stored = loadFromStorage(storageKey);
    return stored?.changedFiles || [];
  });
  const [messages, setMessages] = useState(() => {
    const stored = loadFromStorage(storageKey);
    return stored?.messages || [];
  });
  const [conversationId, setConversationId] = useState(() => {
    const stored = loadFromStorage(storageKey);
    return stored?.conversationId || null;
  });
  const [error, setError] = useState(null);
  const [currentThought, setCurrentThought] = useState('');
  const [dbLoaded, setDbLoaded] = useState(false);
  const eventSourceRef = useRef(null);

  // Pending changes queue for preview mode - initialize from localStorage
  const [pendingChanges, setPendingChanges] = useState(() => {
    const stored = loadFromStorage(storageKey);
    return stored?.pendingChanges || [];
  });

  // Staged files - files that have been written but not committed
  const [stagedFiles, setStagedFiles] = useState(() => {
    const stored = loadFromStorage(storageKey);
    return stored?.stagedFiles || [];
  });

  // Pending clarification - agent needs user input before continuing
  const [pendingClarification, setPendingClarification] = useState(null);

  // Load from DB if localStorage is empty and brandId is provided
  useEffect(() => {
    if (dbLoaded) return;
    if (!brandId) {
      setDbLoaded(true);
      return;
    }
    
    const stored = loadFromStorage(storageKey);
    if (stored && stored.messages?.length > 0) {
      // localStorage has data, no need to load from DB
      setDbLoaded(true);
      return;
    }

    // Try loading from DB
    loadFromDB(brandId).then((dbData) => {
      if (dbData && dbData.messages?.length > 0) {
        console.debug('[Agent] Loaded conversation from DB');
        setStatus(dbData.status === 'running' ? 'idle' : dbData.status);
        setSteps(dbData.steps || []);
        setChangedFiles(dbData.changedFiles || []);
        setMessages(dbData.messages || []);
        setConversationId(dbData.conversationId);
        // Also save to localStorage for faster future loads
        saveToStorage(storageKey, dbData);
      }
      setDbLoaded(true);
    });
  }, [brandId, storageKey, dbLoaded]);

  // Save to localStorage whenever state changes
  useEffect(() => {
    // Save even during running so we can resume if page reloads
    saveToStorage(storageKey, {
      conversationId,
      status,
      steps,
      changedFiles,
      stagedFiles,
      messages,
      pendingChanges,
    });
  }, [status, steps, changedFiles, stagedFiles, messages, conversationId, storageKey, pendingChanges]);

  // Sync to DB when agent completes (non-blocking)
  useEffect(() => {
    if (!brandId) return;
    if (status === 'done' || status === 'error') {
      saveToDB(brandId, {
        conversationId,
        status: status === 'done' ? 'completed' : 'error',
        messages,
        steps,
        changedFiles,
        pendingChanges,
        error,
      }).then((result) => {
        // If a new conversation was created, store the ID
        if (result?.conversationId && !conversationId) {
          setConversationId(result.conversationId);
        }
      });
    }
  }, [status, brandId, conversationId, messages, steps, changedFiles, error, pendingChanges]);

  const runAgent = useCallback(async (prompt, options = {}) => {
    console.log('[SiteAgent] runAgent called with:', { prompt: prompt?.slice(0, 100), brandId, brandSlug, options });
    
    // Reset state for new run (but keep messages and staged files)
    // Staged files are preserved across prompts - only cleared on explicit commit/discard
    setStatus('running');
    setSteps([]);
    // Don't clear changedFiles - these are committed changes
    // Don't clear stagedFiles - preserve across prompts until user commits/discards
    // Don't clear pendingChanges - these accumulate until user accepts/rejects
    setError(null);
    setCurrentThought(brandSlug ? `Starting for ${brandSlug}...` : 'Starting...');

    try {
      console.log('[SiteAgent] Sending request to:', `${API_BASE}/api/site-agent/run`);
      
      // Build conversation history for agent context
      // Include recent messages so agent knows what was discussed
      const conversationHistory = messages.slice(-20).map(msg => ({
        role: msg.role,
        content: msg.content,
        type: msg.type || 'message',
      }));
      
      const response = await fetch(`${API_BASE}/api/site-agent/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          conversationHistory, // Include conversation context
          interactive: options.interactive || false,
          maxSteps: options.maxSteps || 10,
          // Staging mode: files are written immediately but not committed
          // This allows the preview to show changes before user accepts
          stagingMode: options.stagingMode !== false, // Default to staging mode
          // Pass brand context for per-brand sandbox
          brandId: brandId || undefined,
          brandSlug: brandSlug || undefined,
        }),
      });

      if (!response.ok) {
        // Clone response before reading body to avoid "body already used" error
        const clonedResponse = response.clone();
        let errorMessage = `HTTP ${response.status}`;
        try {
          const err = await clonedResponse.json();
          errorMessage = err.error || errorMessage;
        } catch {
          // If JSON parsing fails, try text
          try {
            const text = await response.text();
            if (text) errorMessage = text;
          } catch {
            // Ignore if we can't read the body
          }
        }
        throw new Error(errorMessage);
      }

      console.log('[SiteAgent] Response OK, starting SSE stream read');
      
      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('[SiteAgent] Stream ended, total events received:', eventCount);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        
        // Parse SSE events
        const events = buffer.split('\n\n');
        buffer = events.pop() || ''; // Keep incomplete event in buffer

        for (const eventStr of events) {
          if (!eventStr.trim()) continue;

          const lines = eventStr.split('\n');
          let eventType = '';
          let eventData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7);
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            }
          }

          if (!eventType || !eventData) continue;

          try {
            const data = JSON.parse(eventData);
            eventCount++;
            console.log(`[SiteAgent] SSE event #${eventCount}:`, eventType, JSON.stringify(data).slice(0, 150));
            handleEvent(eventType, data);
          } catch (e) {
            console.warn('[SiteAgent] Failed to parse event data:', eventData, e);
          }
        }
      }

    } catch (err) {
      console.error('[SiteAgent] Error in runAgent:', err);
      setError(err.message);
      setStatus('error');
    }
  }, [brandId, brandSlug]);

  const handleEvent = useCallback((type, data) => {
    console.log('[SiteAgent handleEvent]', type, JSON.stringify(data).slice(0, 200));
    
    switch (type) {
      case 'status':
        setCurrentThought(data.message);
        break;

      case 'thinking':
        // Streaming thought updates during LLM generation
        setCurrentThought(data.thought || data.message || 'Thinking...');
        break;

      case 'step_start':
        setCurrentThought(data.thought);
        setSteps(prev => [
          ...prev,
          {
            number: data.stepNumber,
            thought: data.thought,
            action: null,
            result: null,
            status: 'thinking',
            timestamp: data.timestamp,
          },
        ]);
        break;

      case 'action':
        setCurrentThought(`Executing: ${data.action?.action || 'action'}...`);
        setSteps(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(s => s.number === data.stepNumber);
          if (idx !== -1) {
            updated[idx] = {
              ...updated[idx],
              action: data.action,
              status: 'executing',
            };
          }
          return updated;
        });
        break;

      case 'observation':
        setCurrentThought('Processing result...');
        setSteps(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(s => s.number === data.stepNumber);
          if (idx !== -1) {
            updated[idx] = {
              ...updated[idx],
              result: data.result,
              status: 'complete',
            };
          }
          return updated;
        });
        break;

      case 'file_changed':
        setCurrentThought(`Updated: ${data.path}`);
        if (data.staged) {
          // File was written but not committed - track as staged
          // Update existing entry for the same path, or add new entry
          setStagedFiles(prev => {
            const existingIdx = prev.findIndex(f => f.path === data.path);
            if (existingIdx !== -1) {
              // Update existing entry with latest change
              const updated = [...prev];
              updated[existingIdx] = {
                ...updated[existingIdx],
                action: data.action,
                bytes: data.bytes,
                timestamp: data.timestamp,
                changeCount: (updated[existingIdx].changeCount || 1) + 1,
              };
              return updated;
            }
            // New file - add to staged
            return [
              ...prev,
              {
                path: data.path,
                action: data.action,
                bytes: data.bytes,
                timestamp: data.timestamp,
                changeCount: 1,
              },
            ];
          });
        } else {
          // File was committed - track as changed
          setChangedFiles(prev => {
            const existingIdx = prev.findIndex(f => f.path === data.path);
            if (existingIdx !== -1) {
              // Update existing entry
              const updated = [...prev];
              updated[existingIdx] = {
                ...updated[existingIdx],
                action: data.action,
                timestamp: data.timestamp,
              };
              return updated;
            }
            return [
              ...prev,
              {
                path: data.path,
                action: data.action,
                timestamp: data.timestamp,
              },
            ];
          });
        }
        break;

      case 'pending_change':
        // Add to pending changes queue for user review
        setCurrentThought(`Proposed change: ${data.change?.path || data.path}`);
        setPendingChanges(prev => [
          ...prev,
          {
            ...data.change,
            status: 'pending', // pending | accepted | rejected
            timestamp: data.timestamp || Date.now(),
          },
        ]);
        break;

      case 'clarify':
        // Agent needs clarification - pause and wait for user input
        setStatus('awaiting_clarification');
        setCurrentThought('Waiting for your input...');
        setPendingClarification({
          question: data.question,
          options: data.options,
          default: data.default,
          thought: data.thought,
          stepNumber: data.stepNumber,
          timestamp: data.timestamp,
        });
        // Add clarification request to messages
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.question,
          type: 'clarification',
          options: data.options,
          default: data.default,
          timestamp: data.timestamp,
        }]);
        break;

      case 'respond':
        // Agent sends a response message to the user (shows in chat)
        setCurrentThought('');
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.message,
          type: 'response',
          timestamp: data.timestamp,
        }]);
        break;

      case 'plan':
        // Agent shares a structured plan (shows in chat with formatted steps)
        setCurrentThought('Planning...');
        const planMessage = `**Plan: ${data.summary}**\n\n${data.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}`;
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: planMessage,
          type: 'plan',
          plan: {
            summary: data.summary,
            steps: data.steps,
            proceed: data.proceed,
          },
          timestamp: data.timestamp,
        }]);
        break;

      case 'agent_done':
        setStatus('done');
        setCurrentThought(data.success ? 'Task completed!' : 'Task finished with issues');
        // Add agent completion message to conversation
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.success ? 'Task completed successfully.' : 'Task finished with some issues.',
          timestamp: Date.now(),
          totalSteps: data.totalSteps,
        }]);
        break;

      case 'done':
        setStatus(data.success ? 'done' : 'error');
        break;

      case 'error':
        setError(data.message);
        setCurrentThought(`Error: ${data.message}`);
        break;

      case 'log':
        // Update current thought with log messages for better feedback
        if (data.message && !data.message.startsWith('>') && !data.message.includes('npm')) {
          setCurrentThought(data.message);
        }
        console.debug('[Agent]', data.message);
        break;

      case 'result':
        // Final result JSON - this usually means the agent has finished
        console.log('[Agent Result]', data.data);
        if (data.data?.success !== undefined) {
          setStatus(data.data.success ? 'done' : 'error');
          setCurrentThought(data.data.success ? 'Task completed!' : 'Task finished with issues');
          if (data.data.history) {
            // Update steps from final result
            setSteps(data.data.history.map((h, i) => ({
              stepNumber: i + 1,
              thought: h.step?.thought,
              action: h.step?.action,
              result: h.result,
              durationMs: h.durationMs,
            })));
          }
        }
        break;

      default:
        console.debug(`[Agent Event] ${type}:`, data);
    }
  }, []);

  const stop = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setStatus('idle');
    setCurrentThought('Stopped');
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setSteps([]);
    setChangedFiles([]);
    setStagedFiles([]);
    setPendingChanges([]);
    setMessages([]);
    setConversationId(null);
    setError(null);
    setCurrentThought('');
    // Clear localStorage
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
  }, [storageKey]);

  // Function to add a user message
  const addMessage = useCallback((content) => {
    const newMessage = {
      role: 'user',
      content,
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  }, []);

  // Respond to a clarification request from the agent
  const respondToClarification = useCallback(async (response) => {
    if (!pendingClarification) return;

    // Add user's response to messages
    const userMessage = {
      role: 'user',
      content: response,
      type: 'clarification_response',
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Clear the pending clarification
    const originalQuestion = pendingClarification.question;
    setPendingClarification(null);

    // Get the last user message (the original goal) from messages
    const lastGoal = messages.find(m => m.role === 'user' && m.type !== 'clarification_response')?.content;
    
    // Re-run the agent with the original goal plus the clarification context
    const enrichedPrompt = lastGoal 
      ? `${lastGoal}\n\nClarification: When you asked "${originalQuestion}", the user responded: "${response}"`
      : response;

    await runAgent(enrichedPrompt);
  }, [pendingClarification, messages, runAgent]);

  // Accept a pending change - apply it to the sandbox
  const acceptChange = useCallback(async (changeId) => {
    const change = pendingChanges.find(c => c.changeId === changeId);
    if (!change || !brandId) return null;

    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          changeId: change.changeId,
          path: change.path,
          action: change.action,
          newContent: change.newContent,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // Update pending change status
      setPendingChanges(prev => prev.map(c => 
        c.changeId === changeId ? { ...c, status: 'accepted' } : c
      ));

      // Add to changed files
      setChangedFiles(prev => [
        ...prev,
        {
          path: change.path,
          action: change.action,
          timestamp: Date.now(),
          commitHash: result.commitHash,
        },
      ]);

      return result;
    } catch (err) {
      console.error('Failed to accept change:', err);
      setError(err.message);
      return null;
    }
  }, [brandId, pendingChanges]);

  // Reject a pending change - remove from queue
  const rejectChange = useCallback((changeId) => {
    setPendingChanges(prev => prev.map(c => 
      c.changeId === changeId ? { ...c, status: 'rejected' } : c
    ));
  }, []);

  // Accept all pending changes
  const acceptAll = useCallback(async () => {
    const pending = pendingChanges.filter(c => c.status === 'pending');
    if (pending.length === 0 || !brandId) return null;

    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/apply`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          changes: pending.map(c => ({
            changeId: c.changeId,
            path: c.path,
            action: c.action,
            newContent: c.newContent,
          })),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // Update all pending changes to accepted
      setPendingChanges(prev => prev.map(c => 
        c.status === 'pending' ? { ...c, status: 'accepted' } : c
      ));

      // Add to changed files
      setChangedFiles(prev => [
        ...prev,
        ...pending.map(c => ({
          path: c.path,
          action: c.action,
          timestamp: Date.now(),
          commitHash: result.commitHash,
        })),
      ]);

      return result;
    } catch (err) {
      console.error('Failed to accept all changes:', err);
      setError(err.message);
      return null;
    }
  }, [brandId, pendingChanges]);

  // Reject all pending changes
  const rejectAll = useCallback(() => {
    setPendingChanges(prev => prev.map(c => 
      c.status === 'pending' ? { ...c, status: 'rejected' } : c
    ));
  }, []);

  // Commit all staged changes (git commit)
  const commitAll = useCallback(async (message) => {
    if (stagedFiles.length === 0 || !brandId) return null;

    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'commit',
          message: message || 'Agent changes committed by user',
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // Move staged files to changed files
      setChangedFiles(prev => [
        ...prev,
        ...stagedFiles.map(f => ({
          ...f,
          commitHash: result.hash,
          committed: true,
        })),
      ]);

      // Clear staged files
      setStagedFiles([]);

      return result;
    } catch (err) {
      console.error('Failed to commit staged changes:', err);
      setError(err.message);
      return null;
    }
  }, [brandId, stagedFiles]);

  // Commit a single staged file
  const commitFile = useCallback(async (filePath, message) => {
    if (!filePath || !brandId) return null;

    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/git`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'commit-file',
          path: filePath,
          message: message || `Commit ${filePath}`,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // Move this file from staged to changed
      setStagedFiles(prev => prev.filter(f => f.path !== filePath));
      setChangedFiles(prev => [
        ...prev,
        {
          path: filePath,
          action: 'committed',
          commitHash: result.hash,
          committed: true,
          timestamp: Date.now(),
        },
      ]);

      return result;
    } catch (err) {
      console.error('Failed to commit file:', err);
      setError(err.message);
      return null;
    }
  }, [brandId]);

  // Discard all staged changes (git checkout)
  const discardAll = useCallback(async () => {
    if (stagedFiles.length === 0 || !brandId) return null;

    try {
      const response = await fetch(`${API_BASE}/api/brands/${brandId}/sandbox/discard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // Clear staged files - they've been reverted
      setStagedFiles([]);

      return result;
    } catch (err) {
      console.error('Failed to discard staged changes:', err);
      setError(err.message);
      return null;
    }
  }, [brandId, stagedFiles]);

  // Get counts
  const pendingCount = pendingChanges.filter(c => c.status === 'pending').length;
  const acceptedCount = pendingChanges.filter(c => c.status === 'accepted').length;
  const rejectedCount = pendingChanges.filter(c => c.status === 'rejected').length;
  const stagedCount = stagedFiles.length;

  return {
    status,
    steps,
    changedFiles,
    stagedFiles,
    pendingChanges,
    pendingClarification,
    messages,
    error,
    currentThought,
    runAgent,
    stop,
    reset,
    addMessage,
    respondToClarification,
    acceptChange,
    rejectChange,
    acceptAll,
    rejectAll,
    commitAll,
    commitFile,
    discardAll,
    pendingCount,
    acceptedCount,
    rejectedCount,
    stagedCount,
    isRunning: status === 'running',
    isDone: status === 'done',
    isAwaitingClarification: status === 'awaiting_clarification',
    hasError: status === 'error' || !!error,
    hasPendingChanges: pendingCount > 0,
    hasStagedChanges: stagedCount > 0,
    hasPendingClarification: !!pendingClarification,
  };
}

export default useSiteAgent;

